<template>
  <div>

  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <div class="box" v-if="availableReminderMethods.length <= 0">
      You must setup available methods ({{ this.config.reminderMethods.join(', ') }}) in settings before you can use reminders.
      <router-link v-if="hasPermission('site-settings.edit')" class="text-link" to="/administration/settings/reminders">Reminder settings</router-link>
    </div>
    <div class="box" v-else>
      <div class="flex flex-wrap -mx-2">

        <div class="w-full md:w-1/3 pr-2 border-r border-theme-200 light:border-theme-600">
          <p class="text-2xl font-bold mb-1">Recipient type</p>
          <button
            v-for="(type, i) in ['individual', 'list']"
            :key="'recipient-type-' + i"
            class="btn text-lg px-4 focus:outline-none mr-1"
            :class="recipientType === type ? 'btn-purple' : 'border border-gray-700 light:border-gray-300'"
            @click="recipientType = type"
          >{{ type | sentenceCase }}</button>

          <template v-if="recipientType === 'individual'">
            <p class="text-lg font-bold mb-1 mt-4">Method</p>
            <button
              v-for="(method, i) in availableReminderMethods"
              :key="'reminder-methods-' + i"
              class="btn focus:outline-none mr-1"
              :class="reminder.method === method ? 'btn-purple' : 'border border-gray-700 light:border-gray-300'"
              @click="reminder.method = method"
            >{{ method }}</button>
          </template>

          <template v-if="recipientType === 'individual'">
            <model-select
              class="mt-8"
              model="customer"
              form-name="customer"
              v-model="reminder.contact"
              :list.sync="customerList"
              :search-on-mount="true"
              :is-taggable="!inModal"
              :button-new="!inModal"
              :button-edit="!inModal"
            ></model-select>

            <div class="mt-4" v-if="selectedCustomer && reminder.method === 'sms'">
              <div v-if="customer.phone_numbers.length > 1">
                <label class="block text-xs font-bold mb-2" for="phone-number">
                  Select phone number
                </label>
                <div class="relative">
                  <select
                    class="form-control pr-8"
                    id="phone-number"
                    v-model="phoneNumberID"
                  >
                    <option :value="phone.id" v-for="(phone, i) in customer.phone_numbers" :key="'phone-numbers-' + i">
                      <span v-if="phone.countryData">
                        + {{ phone.countryData.calling_code }}
                      </span>
                      {{ phone.number }}
                    </option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center text-xs px-2">
                    <i class="fas fa-chevron-down"></i>
                  </div>
                </div>
              </div>
              <div v-else>
                <p class="text-sm font-bold">Phone number</p>
                <p class="text-lg">
                  <span v-if="customer.phone_numbers[0].countryData">
                    + {{ customer.phone_numbers[0].countryData.calling_code }}
                  </span>
                  {{ customer.phone_numbers[0].number }}
                </p>
              </div>
            </div>
          </template>
          <template v-else-if="recipientType === 'list'">
            <model-select
              class="mt-4"
              model="reminder-list"
              form-name="reminder_list"
              data-vv-as="Reminder list"
              v-model="list"
              :list.sync="reminderListList"
              :search-on-mount="true"
              :is-taggable="!inModal"
              :button-new="false"
              :button-edit="!inModal"
            ></model-select>
          </template>
        </div>

        <div class="w-full md:w-2/3 pl-2" v-if="(reminder.method && selectedCustomer || selectedList)">

          <p class="text-xl font-bold mt-0 border-b border-theme-200 light:border-theme-600 mb-4" v-if="selectedCustomer">
            <span class="uppercase">{{ reminder.method }}</span>
            <span class="font-normal text-sm ml-1" v-if="reminder.method === 'e-mail' && customer.email">
              ({{ customer.email }})
            </span>
            <span class="font-normal text-sm ml-1" v-if="reminder.method === 'sms' && phoneNumberID">
              (
                <span v-if="selectedPhone.countryData">
                  {{ selectedPhone.countryData.calling_code }}
                </span>
                {{ selectedPhone.number }})
            </span>
          </p>

          <template v-if="selectedCustomer && reminder.method === 'e-mail' && !customer.email">
            <p class="text-sm">
              <span><i class="fas fa-exclamation-triangle text-red-500"></i></span>
              Customer doesn't have an email. <strong>Please fill their email</strong> or choose a different reminder method
            </p>

            <div class="form-group mt-1 mb-2">
              <ValidationProvider
                rules="required|email"
                name="email"
                v-slot="{ errors }"
              >
                <input
                  name="email"
                  type="text"
                  id="email"
                  class="form-control"
                  v-model="newCustomerEmail"
                >
                <span class="text-sm text-red-500">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <button type="button" class="btn btn-green py-2 px-3" @click="saveCustomerEmail">
              <i class="fas fa-check"></i>
              <span class="ml-2">Set email</span>
            </button>
          </template>

          <template v-else>
            <div class="row">
              <div class="col-xl-6" v-if="reminder.method === 'e-mail'">
                <div class="form-group mb-3">
                  <label class="block text-xs font-bold mb-2" for="subject">
                    Subject <span class="text-red-500">*</span>
                  </label>

                  <ValidationProvider
                    rules="required"
                    name="subject"
                    v-slot="{ errors }"
                  >
                    <input
                      name="subject"
                      type="text"
                      id="subject"
                      class="form-control"
                      v-model="reminder.subject"
                    >
                    <span class="text-sm text-red-500">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="col-xl-6">
                <div class="form-group mb-3">
                  <label class="block text-xs font-bold mb-2" for="due-date">
                    Due date <span class="text-red-500">*</span>
                  </label>
                  <ValidationProvider
                    rules="required"
                    name="due date"
                    v-slot="{ errors }"
                  >
                    <flat-pickr
                      v-model="dueDate"
                      :config="flatpickrConfig"
                      class="form-control"
                      placeholder="Select date"
                      id="due-date"
                      name="due-date"
                    >
                    </flat-pickr>
                    <span class="text-sm text-red-500">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-xl-12">
                <div class="form-group mb-3">
                  <label class="block text-xs font-bold mb-2" for="content">
                    Content <span class="text-red-500">*</span>
                  </label>
                  <ValidationProvider
                    rules="required"
                    name="content"
                    v-slot="{ errors }"
                  >
                    <textarea
                      name="content"
                      class="form-control"
                      id="content"
                      rows="15"
                      v-model="reminder.content"
                    ></textarea>
                    <span class="text-sm text-red-500">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </template>

        </div>

      </div>
    </div>
  </ValidationObserver>

    <div class="box flex flex-wrap justify-end items-center" v-if="availableReminderMethods.length > 0">
      <button
        type="button"
        class="btn btn-reset"
        @click="reset"
      >
        <i class="fa-xs fas fa-undo"></i>
        <span class="ml-1">RESET</span>
      </button>
      <button
        type="button"
        class="btn btn-save"
        @click="save"
      >
        <i class="fa-xs fas fa-check"></i>
        <span class="ml-2">{{ mode === 'edit' ? 'UPDATE' : 'SAVE' }}</span>
      </button>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  props: {
    reminderID: {},
    createAlert: Boolean,
    inModal: Boolean,
  },
  data() {
    return {
      mode: null,
      editID: null,
      reminder: {
        contact: {} // use in model-select only
      },
      recipientType: 'individual', // individual, list
      list: {},
      reminderListList: [],
      customer: {},
      customerList: [],
      dueDate: '',
      phoneNumberID: null,
      newCustomerEmail: '', // for setting new email
      flatpickrConfig: {},
      dueDateFormat: 'DD/MM/YYYY HH:mm',
      dueDateFormatFlatpickr: 'd/m/Y H:i',
    }
  },
  watch: {
    'reminder.contact'() {
      this.getCustomer()
    },
  },
  computed: {
    availableReminderMethods(){
      return this.config.reminderMethods.filter(m => {
        if (m === 'sms' && (!this.config.nexmoKey || !this.config.nexmoSecret)) {
          return false
        }
        if (m === 'e-mail' && (!this.config.mailHost || !this.config.mailPort || !this.config.mailUsername || !this.config.mailPassword)) {
          return false
        }
        return true
      })
    },
    selectedCustomer(){
      return this.customer && this.customer.id
    },
    selectedList(){
      return this.list && this.list.id
    },
    selectedPhone() {
      return !this.customer.phone_numbers || this.customer.phone_numbers.filter(p => p.id === this.phoneNumberID)[0]
    },
    ...mapGetters({
      CountryByID: 'countries/countryByID',
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  mounted() {
    this.flatpickrConfig = Object.assign({}, this.config.flatpickrConfig)
    this.flatpickrConfig.dateFormat = this.dueDateFormatFlatpickr
    this.flatpickrConfig.enableTime = true
    this.flatpickrConfig.time_24hr = true
    this.flatpickrConfig.defaultHour = 13
    this.setMode()
  },
  methods: {
    emptyReminder() {
      return {
        contact_id: '',
        contact_phone_number_id: null,
        method: this.config.defaultReminderMethod,
        subject: '',
        content: '',
        due_date: '',
      }
    },
    setMode() {
      const name = this.$route.name.split('.')
      if (name[0] === 'reminder' && name[1]) {
        this.mode = name[1]
      } else {
        this.mode = 'create'
      }

      if (this.reminderID) {
        this.editID = this.reminderID
        this.mode = 'edit'
      } else if (this.$route.params.reminderID) {
        this.editID = this.$route.params.reminderID
      }

      if (this.mode === 'create') {
        this.reminder = this.emptyReminder()
        if (this.availableReminderMethods.length === 1) {
          this.reminder.method = this.availableReminderMethods[0]
        }
      } else if (this.mode === 'edit') {
        // get reminder
        this.get()
      }
    },
    get() {
      axios.get('reminders/' + this.editID)
        .then((response) => {
          const reminder = response.data

          this.reminder = Object.assign({}, reminder)

          this.dueDate = this.$moment(reminder.due_date).format(this.dueDateFormat)

          if (reminder.list) {
            this.recipientType = 'list'
            this.list = reminder.list
            this.list.display_name = '#' + this.list.id + ' ' + this.list.name
          } else if (reminder.contact) {
            this.recipientType = 'individual'
            reminder.contact.display_name = '#' + reminder.contact.id + ' ' + reminder.contact.first_name + ' ' + reminder.contact.last_name + (reminder.contact.company_name ? ' (' + reminder.contact.company_name + ')' : '')
            this.customer = Object.assign({}, reminder.contact)
            this.phoneNumberID = reminder.contact_phone_number_id
          }
        })
        .catch((error) => {
          console.log(error)
          this.$swal.fire({
            title: 'Reminder not found',
            type: 'warning',
          })
        })
    },
    setCustomerData(customer) {
      if (customer.phone_numbers.length) {
        // sort by type (primary first)
        customer.phone_numbers.sort((a) => {
          return a.type === 'primary' ? -1 : 1
        }).map(p => p.countryData = this.CountryByID(p.country_id))

        this.phoneNumberID = customer.phone_numbers[0].id
      }
      this.customer = Object.assign({}, customer)
      this.reminder.contact_id = this.customer.id
    },
    getCustomer() {
      if (!this.reminder.contact || !this.reminder.contact.id) {
        return
      }

      axios.get('customers/' + this.reminder.contact.id)
        .then((response) => {
          this.setCustomerData(response.data)
        })
    },
    _reset() {
      this.customer = {}
      this.phoneNumberID = ''
      this.dueDate = ''
      if (this.mode === 'create') {
        this.reminder = Object.assign({}, this.emptyReminder())
        if (this.availableReminderMethods.length === 1) {
          this.reminder.method = this.availableReminderMethods[0]
        }
      } else {
        this.get()
      }
    },
    reset() {
      this.$swal.fire({
        title: 'Reset Form',
        text: 'Are you sure?',
        type: 'question',
        confirmButtonText: 'Yes',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      })
        .then((result) => {
          if (result.value) {
            this._reset()
          }
        })
    },
    saveCustomerEmail() {
      const cust = Object.assign({}, this.customer)
      cust.email = this.newCustomerEmail

      axios.put('customers/' + cust.id, cust)
        .then((response) => {
          this.setCustomerData(response.data)
        })
    },
    async save () {
      const isValid = await this.$refs.observer.validate()
      if (!isValid) {
        this.$notify({type: 'error', title: 'Form has errors'})
        return
      }
      this._save()
    },
    _save() {
      const dueDate = this.$moment(this.dueDate, this.dueDateFormat)
      if(dueDate.isValid()) {
        this.reminder.due_date = dueDate.format(this.config.dateTimeFormats.api)
      } else {
        this.$swal.fire({
          title: 'Error',
          text: 'Invalid due date',
          type: 'error',
        })
      }

      if (this.phoneNumberID) {
        this.reminder.contact_phone_number_id = this.phoneNumberID
      }

      if (this.selectedList) {
        this.reminder.list_id = this.list.id
        this.reminder.method = null
      }

      // create / update
      if (this.mode === 'create') {
        axios.post('reminders', this.reminder)
          .then((response) => {
            const newReminder = response.data
            this.$emit('created', newReminder)
            this._reset()

            if (this.createAlert) {
              this.$swal.fire({
                title: 'Done',
                text: 'Reminder created successfully!',
                type: 'success',
              })
            } else {
              this.$router.push('/reminder/details/' + newReminder.id)
            }
          })
          .catch((error) => {
            console.log(error)
          })
      } else if (this.mode === 'edit') {
        axios.put('reminders/' + this.reminder.id, this.reminder)
          .then(() => {
            this.$emit('updated', this.reminder)
            this.$swal.fire({
              title: 'Done',
              text: 'Reminder updated successfully!',
              type: 'success',
            })
            this.$router.push('/reminder/details/' + this.reminder.id)
          })
          .catch((error) => {
            console.log(error)
          })
      }
    },
  }
}
</script>
